/**
 * Persist calculated project summary back to the jphph_projects table.
 *
 * After schedule/checklist progress is recalculated, this function saves:
 *   - project_progress = combined project progress (0-100)
 *   - schedule_progress = schedule final progress (0-100)
 *   - checklist_progress = checklist final progress (0-100)
 *   - pending_schedule_items = count of incomplete schedule items
 *   - status = lifecycle status ('completed' if 100%, else 'active' or 'planning')
 *   - current_stage is NOT overwritten here — use Set Stage for manual workflow stage
 *   - updated_at = now()
 *
 * This is called after every schedule completion action so the Dashboard,
 * project list, and any cached UI components immediately show correct data.
 */

import { supabase } from '@/lib/supabase';
import { getMainStage, UNASSIGNED_STAGE } from './scheduleStageMapping';
import { calculateProjectProgress, isScheduleItemComplete } from './projectProgress';
import { deduplicateChecklistItems } from './checklistDedup';
import { jphDebugLog } from './jphDebug';
import type { ScheduleItemLike, ChecklistItemLike } from './projectProgress';

export async function persistProjectSummary(projectId: string): Promise<{
  projectProgress: number;
  scheduleProgress: number;
  checklistProgress: number;
  pendingItems: number;
  activeStage: string;
  status: string;
}> {
  // 1. Fetch fresh schedule items (exclude soft-deleted)
  const { data: scheduleRows } = await supabase
    .from('jphph_project_schedule_items')
    .select('id, is_complete, progress, actual_end_date, main_stage, phase, is_included')
    .eq('project_id', projectId)
    .eq('is_deleted', false);

  // 2. Fetch fresh checklist items
  const { data: checklistRows } = await supabase
    .from('jphph_checklist_items')
    .select('id, management_checked, requires_management_check, subcontractor_checked, requires_subcontractor_check, source_template_item_id')
    .eq('project_id', projectId);

  // CRITICAL FIX: Deduplicate using shared utility (handles NULL template IDs)
  const rawChecklist = checklistRows || [];
  const { unique: dedupedChecklist } = deduplicateChecklistItems(rawChecklist);

  // 3. Fetch mappings
  const scheduleIds = (scheduleRows || []).map((s) => s.id);
  let mappingRows: Array<{ project_schedule_item_id: string; project_checklist_item_id: string }> = [];

  if (scheduleIds.length > 0) {
    const CHUNK = 100;
    for (let i = 0; i < scheduleIds.length; i += CHUNK) {
      const batch = scheduleIds.slice(i, i + CHUNK);
      const { data: mappings } = await supabase
        .from('jphph_checklist_schedule_item_mappings')
        .select('project_schedule_item_id, project_checklist_item_id')
        .in('project_schedule_item_id', batch);
      if (mappings) mappingRows.push(...(mappings as typeof mappingRows));
    }
  }

  // 4. Build linked checklist map
  const checklistById = new Map((checklistRows || []).map((c) => [c.id, c]));
  const linkedMap = new Map<string, ChecklistItemLike[]>();
  for (const m of mappingRows) {
    if (!m.project_schedule_item_id || !m.project_checklist_item_id) continue;
    const ci = checklistById.get(m.project_checklist_item_id);
    if (!ci) continue;
    const arr = linkedMap.get(m.project_schedule_item_id) || [];
    arr.push({
      id: ci.id,
      management_checked: ci.management_checked ?? false,
      requires_management_check: ci.requires_management_check,
      subcontractor_checked: ci.subcontractor_checked ?? false,
      requires_subcontractor_check: ci.requires_subcontractor_check,
    });
    linkedMap.set(m.project_schedule_item_id, arr);
  }

  const scheduleItems: ScheduleItemLike[] = (scheduleRows || []).map((s) => ({
    id: s.id,
    is_complete: s.is_complete ?? false,
    progress: s.progress ?? 0,
    main_stage: s.main_stage || null,
    phase: s.phase || null,
    is_included: s.is_included !== false,
    actual_end_date: s.actual_end_date || null,
  }));

  const checklistItems: ChecklistItemLike[] = (dedupedChecklist).map((c) => ({
    id: c.id,
    management_checked: c.management_checked ?? false,
    requires_management_check: c.requires_management_check,
    subcontractor_checked: c.subcontractor_checked ?? false,
    requires_subcontractor_check: c.requires_subcontractor_check,
  }));

  // 5. Calculate
  const result = calculateProjectProgress(scheduleItems, checklistItems, linkedMap);

  // 6. Pending count
  const includedSchedule = scheduleItems.filter((s) => s.is_included !== false);
  const pendingItems = includedSchedule.filter((si) => {
    const linked = linkedMap.get(si.id) || [];
    return !isScheduleItemComplete(si, linked);
  }).length;

  // 7. Determine active stage
  const STAGE_ORDER = ['Planning', 'Design', 'Consent', 'Construction', 'Handover'];
  let activeStage = 'Handover';

  // CRITICAL FIX: If project is 100% complete, force stage to 'Completed'
  const isFullyComplete = result.combinedPct === 100;

  if (isFullyComplete) {
    activeStage = 'Completed';
  } else {
    for (const stageName of STAGE_ORDER) {
      const stageItems = includedSchedule.filter((si) => {
        const stage = getMainStage(si);
        if (stage === UNASSIGNED_STAGE) return false;
        return stage === stageName;
      });
      if (stageItems.length === 0) continue;
      const allComplete = stageItems.every((si) => {
        const linked = linkedMap.get(si.id) || [];
        return isScheduleItemComplete(si, linked);
      });
      if (!allComplete) {
        activeStage = stageName;
        break;
      }
    }
  }

  // 8. Determine status
  let status = 'planning';
  if (isFullyComplete) {
    status = 'completed';
  } else if (result.combinedPct > 0) {
    status = 'active';
  }

  // 9. Persist to DB
  const now = new Date().toISOString();
  const { error: updateErr } = await supabase
    .from('jphph_projects')
    .update({
      project_progress: result.combinedPct,
      schedule_progress: result.scheduleFinalPct,
      checklist_progress: result.checklistFinalPct,
      pending_schedule_items: pendingItems,
      status,
      updated_at: now,
    })
    .eq('id', projectId);

  if (updateErr) {
    // eslint-disable-next-line no-console
    console.error('[persistProjectSummary] DB update error:', updateErr.message);
  } else {
    jphDebugLog('PROJECT SUMMARY - SHARED', {
      projectId,
      scheduleFinalProgress: result.scheduleFinalPct,
      checklistFinalProgress: result.checklistFinalPct,
      projectProgress: result.combinedPct,
      pendingScheduleItems: pendingItems,
      activeStage,
      projectStatus: status,
      source: 'persistProjectSummary',
    });
  }

  return {
    projectProgress: result.combinedPct,
    scheduleProgress: result.scheduleFinalPct,
    checklistProgress: result.checklistFinalPct,
    pendingItems,
    activeStage,
    status,
  };
}